import { useState } from "react";
import type { UiKey } from "../i18n/ui";
import type { NamedScenarioEntry } from "../lib/namedScenarios";
import type { PersistedUi } from "../lib/persistedUi";

type Props = {
  t: (key: UiKey) => string;
  entries: NamedScenarioEntry[];
  onSave: (name: string, snapshot: PersistedUi) => void;
  onLoad: (snapshot: PersistedUi) => void;
  onDelete: (id: string) => void;
  currentSnapshot: PersistedUi;
};

export function NamedScenariosPanel({
  t,
  entries,
  onSave,
  onLoad,
  onDelete,
  currentSnapshot,
}: Props) {
  const [name, setName] = useState("");

  return (
    <div className="named-scen">
      <h2>{t("namedScenarios.title")}</h2>
      <p className="hint">{t("namedScenarios.hint")}</p>
      <div className="save-row">
        <input
          type="text"
          className="name-input"
          placeholder={t("namedScenarios.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          aria-label={t("namedScenarios.namePlaceholder")}
        />
        <button
          type="button"
          className="btn"
          onClick={() => {
            const n = name.trim();
            if (!n) return;
            onSave(n, currentSnapshot);
            setName("");
          }}
        >
          {t("namedScenarios.save")}
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="empty">{t("namedScenarios.empty")}</p>
      ) : (
        <ul className="list">
          {entries.map((e) => (
            <li key={e.id}>
              <span className="nm">{e.name}</span>
              <div className="actions">
                <button
                  type="button"
                  className="linkish"
                  onClick={() => onLoad(e.snapshot)}
                >
                  {t("namedScenarios.load")}
                </button>
                <button
                  type="button"
                  className="linkish danger"
                  onClick={() => onDelete(e.id)}
                >
                  {t("namedScenarios.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="max-note">{t("namedScenarios.max")}</p>
      <style>{`
        .named-scen h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 0.65rem;
          font-size: 0.82rem;
          color: var(--muted);
          line-height: 1.45;
        }
        .save-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.85rem;
        }
        .name-input {
          flex: 1 1 10rem;
          min-width: 0;
          padding: 0.45rem 0.6rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--surface2);
          color: var(--text);
        }
        .name-input:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 1px;
        }
        .btn {
          border: 1px solid var(--accent-dim);
          background: var(--accent-chip);
          color: var(--accent);
          padding: 0.45rem 0.85rem;
          border-radius: 8px;
          cursor: pointer;
          font: inherit;
          font-weight: 600;
          font-size: 0.88rem;
        }
        .empty {
          margin: 0;
          font-size: 0.88rem;
          color: var(--muted);
        }
        .list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .list li {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          padding: 0.55rem 0;
          border-bottom: 1px solid var(--border);
        }
        .nm {
          font-weight: 600;
        }
        .actions {
          display: flex;
          gap: 0.65rem;
        }
        .linkish {
          border: none;
          background: none;
          padding: 0;
          color: var(--accent);
          cursor: pointer;
          font: inherit;
          font-size: 0.85rem;
          font-weight: 600;
          text-decoration: underline;
        }
        .linkish.danger {
          color: var(--danger);
        }
        .max-note {
          margin: 0.65rem 0 0;
          font-size: 0.75rem;
          color: var(--muted);
        }
      `}</style>
    </div>
  );
}
